// sample-picker.js — R2 sample-picker modal (window.AG_SAMPLE_PICKER).
//
// WRITTEN TO PORT 1:1 as theme assets/sample-picker.js: no prototype-only code
// paths — the cart adapter is injected at open(), so mock (AG_SAMPLES.cart) vs
// real (/cart/add.js + /cart.js) swaps without touching this file. The component
// self-injects AGSP_CSS at open(); theme assets/sample-picker.css carries only
// the render-time link styles.
//
// API:
//   AG_SAMPLE_PICKER.open({ samples, unavailable, cart, onAdded })
//     samples:     [{code,name,variantId,price(cents),image,mode}]  (the PDP blob;
//                  mode 'domestic'|'factory' drives the lead line — see leadLine())
//     unavailable: [{code,name,image,state:'soldout'|'none'}]  (optional — R2.1
//                  grays: rendered AFTER samples as disabled, price-less cells;
//                  'soldout' = program finish at 0 stock, 'none' = no sample)
//     cart:    { add(items)->Promise, read()->Promise({items:[{id,...}]}) }
//     onAdded: fn(addedItems) — theme version opens the cart drawer here
//   AG_SAMPLE_PICKER.directAdd({ sample, cart, onAdded, onSoldOut })
//     the exactly-1-orderable-AND-zero-grays path: no modal, straight cart add.
//
// Brand: .agsp- namespace (R1's .agsr- popout stays archived untouched). Square
// corners, hairlines, Playfair heading, Alata UC CTA, sage selection ring, no
// urgency register. E1 shell mechanics per R1 popout.js (backdrop, ESC/backdrop
// close). Mobile: content-height bottom sheet capped at 100dvh (locked memory:
// dvh not vh; width: not max-width:) — R2.1, per A: sheet must hug content at
// low sample counts instead of forcing full height.
//
// R3-06 (2026-07-18, A's winner pick): DESKTOP joins the finish-panel family —
// 640px sheet-family shell (hairline head, 40px circular close) + a live
// .agsp-prev panel card (photo / FINISH eyebrow / Playfair name / tone pill /
// use-case / texture / material — blob v3 fields) above the grid, following
// orderable-cell hover/click. Everything scoped @media (min-width:561px) with
// .agsp-prev display:none at base — the approved ≤560px sheet is pixel-identical.
// (Gray cells are disabled buttons: browsers suppress their mouse events, so
// grays don't drive the preview — matches the R3-06 prototype A approved.)
//
// R4.0 (2026-08-15, A's picker ruling): the catalog now carries BOTH domestic
// (stocked at Mullica Hill) and factory (made-to-order) samples, so one lead
// time on the link is no longer true. Every cell gains a status line under the
// name in ONE "arrives" register — "Arrives within a week" / "Arrives in 2–3
// weeks" / "Out of stock" / "Unavailable" — and the link drops its tail entirely
// on a mixed PDP. Mode is data (custom.sample_mode on the sample product), never
// an inference. See leadLine().
(function () {
  'use strict';

  var AGSP_CSS = [
    // R2.2 entrance/exit: backdrop fades, panel rises 12px (desktop) / slides up
    // from the bottom edge (mobile sheet). Standard easing only — no bounce,
    // no spring (brand). .is-open added one frame after mount; close() removes
    // it and defers the DOM removal past the transition.
    '.agsp-backdrop{position:fixed;inset:0;background:rgba(36,43,38,0.4);z-index:99990;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity 0.24s cubic-bezier(0.4,0,0.2,1);}',
    '.agsp-backdrop.is-open{opacity:1;}',
    '.agsp-panel{background:#fff;width:520px;border:1px solid rgba(0,0,0,0.1);padding:0;display:flex;flex-direction:column;max-height:min(86vh,720px);opacity:0;transform:translateY(12px);transition:opacity 0.24s cubic-bezier(0.4,0,0.2,1),transform 0.24s cubic-bezier(0.4,0,0.2,1);}',
    '.agsp-backdrop.is-open .agsp-panel{opacity:1;transform:none;}',
    '.agsp-head{display:flex;align-items:flex-start;justify-content:space-between;padding:26px 28px 0;}',
    '.agsp-title{font-family:"Playfair Display",Georgia,serif;font-size:24px;font-weight:600;color:#242B26;margin:0;}',
    '.agsp-close{background:none;border:0;padding:4px;cursor:pointer;color:#242B26;opacity:0.65;font-size:20px;line-height:1;transition:opacity 0.2s;}',
    '.agsp-close:hover{opacity:1;}',
    '.agsp-intro{font-family:"Inter Tight",sans-serif;font-size:14px;line-height:1.5;color:#242B26;margin:8px 0 0;padding:0 28px;}',
    '.agsp-msg{font-family:"Inter Tight",sans-serif;font-size:12.5px;color:#7a3a3a;padding:10px 28px 0;min-height:0;}',
    '.agsp-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;padding:18px 28px 6px;overflow-y:auto;}',
    // r3 (2026-07-17, A): fixed 72px cell = the template height (img 52 +
    // 2×10 padding) — every cell identical; overflow guard so a long wrapped
    // name can never grow one cell past the others.
    '.agsp-cell{position:relative;display:flex;align-items:center;gap:12px;border:1px solid rgba(0,0,0,0.1);background:#fff;padding:10px;cursor:pointer;text-align:left;transition:border-color 0.2s;height:72px;overflow:hidden;}',
    '.agsp-cell:hover{border-color:#465849;}',
    '.agsp-cell.is-selected{box-shadow:inset 0 0 0 1px #465849;border-color:#465849;}',
    '.agsp-cell.is-selected::after{content:"";position:absolute;top:0;right:0;width:14px;height:14px;background:#465849;}',
    '.agsp-cell:disabled{cursor:default;border-color:rgba(0,0,0,0.08);}',
    '.agsp-cell:disabled .agsp-cell-img{opacity:0.5;}',
    '.agsp-cell-img{width:52px;height:52px;object-fit:cover;flex:none;border:1px solid rgba(0,0,0,0.1);background:#f9fafb;}',
    // r4 (2026-07-17, A): ONE-line cells — the text wrapper is a flex row:
    // name+(code) left, price/flag right-aligned on the same line.
    '.agsp-cell-text{flex:1;min-width:0;display:flex;align-items:center;justify-content:space-between;gap:10px;}',
    // R4.0 (A, 2026-08-15): the left slot is a two-row stack — name line on top,
    // the per-finish status/lead line under it. The right slot is unchanged
    // (price + the cart-state flag), so the two axes never share a line.
    '.agsp-cell-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;}',
    '.agsp-cell-name{font-family:Alata,sans-serif;font-size:13px;letter-spacing:0.8px;text-transform:uppercase;color:#242B26;line-height:1.35;min-width:0;}',
    // Lead/status register: prose, so Inter Tight (the panel's paragraph font),
    // muted. State is carried by COLOUR on the same line — never by a second
    // element — so every cell reads at the same rhythm.
    '.agsp-cell-lead{font-family:"Inter Tight",sans-serif;font-size:11.5px;line-height:1.25;color:#6B6B6B;}',
    '.agsp-cell-lead.is-out{color:#7a3a3a;}',
    '.agsp-cell-lead.is-na{color:#6B6B6B;}',
    // r3: code rides INLINE on the name line — "REDWOOD (9010)" — instead of
    // its own row (A's format; collapses one vertical line per cell).
    '.agsp-cell-code{display:inline;font-family:"Inter Tight",sans-serif;font-size:12px;color:#465849;letter-spacing:0;margin:0;}',
    '.agsp-cell-price{font-family:"Inter Tight",sans-serif;font-size:12px;color:#242B26;margin:0;flex:none;}',
    '.agsp-cell-flag{font-family:Alata,sans-serif;font-size:10.5px;letter-spacing:0.8px;text-transform:uppercase;color:#465849;margin:0;flex:none;}',
    '.agsp-cell-flag.is-out{color:#7a3a3a;}',
    '.agsp-cell-flag.is-na{color:#6B6B6B;}',
    '.agsp-foot{padding:16px 28px 26px;margin-top:8px;}',
    '.agsp-cta{display:block;width:100%;background:#4B544C;color:#fff;border:0;padding:15px 20px;font-family:Alata,sans-serif;font-size:13px;letter-spacing:1px;text-transform:uppercase;cursor:pointer;transition:background 0.2s;}',
    '.agsp-cta:hover{background:#242B26;}',
    '.agsp-cta:disabled{opacity:0.5;cursor:not-allowed;}',
    '.agsp-credit{font-family:"Inter Tight",sans-serif;font-size:12px;color:#6B6B6B;text-align:center;margin:0 0 10px;}',
    // R3.1 (A, 2026-07-18): thin scrollbars inside the picker — desktop grid AND
    // the mobile sheet's scrolling list. Exact recipe from the customization
    // modal (customization-modal.css:2232-2244, Round 5 iter6): 6px, square
    // thumb rgba(0,0,0,0.25) -> 0.4 on hover, transparent track.
    '.agsp-panel,.agsp-panel *{scrollbar-width:thin;scrollbar-color:rgba(0,0,0,0.25) transparent;}',
    '.agsp-panel::-webkit-scrollbar,.agsp-panel ::-webkit-scrollbar{width:6px;height:6px;}',
    '.agsp-panel::-webkit-scrollbar-track,.agsp-panel ::-webkit-scrollbar-track{background:transparent;}',
    '.agsp-panel::-webkit-scrollbar-thumb,.agsp-panel ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.25);border-radius:0;}',
    '.agsp-panel::-webkit-scrollbar-thumb:hover,.agsp-panel ::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,0.4);}',
    // R3-06 desktop-only block (min-width:561px) — the ≤560px sheet below stays
    // byte-identical. Shell values from the approved finish-sheet dialog
    // (finishing-display.css:382-472); preview typography from .ag-finish-panel
    // (finishing-display.css:735-782). max-width guards the 561-767px band.
    '.agsp-prev{display:none;}',
    '@media (min-width:561px){',
    '.agsp-panel{width:640px;max-width:calc(100vw - 32px);}',
    '.agsp-head{padding:22px 28px 16px;border-bottom:1px solid #e6e6e3;align-items:center;}',
    '.agsp-close{width:40px;height:40px;border:1px solid rgba(0,0,0,0.15);border-radius:100px;background:#fff;opacity:1;font-size:18px;display:flex;align-items:center;justify-content:center;padding:0;flex:none;transition:border-color 0.2s;}',
    '.agsp-close:hover{border-color:#242B26;}',
    '.agsp-intro{padding:16px 28px 0;margin:0;}',
    '.agsp-grid{padding:16px 28px 6px;}',
    // R3.2 (A, 2026-07-19): vertically compact card — same elements/roles,
    // tighter rhythm. Photo column 220->180, min-height 200->150, body pad
    // 18/20->12/16, name at the Playfair 20px floor, use-case clamped to 2
    // lines. "Make it smaller vertically, keep the aesthetics."
    '.agsp-prev{display:grid;grid-template-columns:180px 1fr;margin:14px 28px 0;border:1px solid rgba(36,43,38,0.12);min-height:150px;flex:none;}',
    '.agsp-prev-photo{position:relative;background:#e8e8e6;overflow:hidden;min-height:150px;}',
    '.agsp-prev-photo img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;}',
    '.agsp-prev-body{padding:12px 16px;min-width:0;}',
    '.agsp-prev-eyebrow{font-family:Alata,sans-serif;font-size:11px;letter-spacing:0.14em;text-transform:uppercase;color:#6B6B6B;margin-bottom:3px;}',
    '.agsp-prev-name{font-family:"Playfair Display",Georgia,serif;font-size:20px;font-weight:600;line-height:1.1;color:#242B26;}',
    '.agsp-prev-code{font-family:"Inter Tight",sans-serif;font-size:12px;font-weight:400;color:#465849;margin-left:4px;}',
    '.agsp-prev-divider{height:1px;background:#e6e6e3;margin:8px 0;}',
    '.agsp-prev-pill{display:inline-block;font-family:Alata,sans-serif;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#4A564D;border:1px solid #4A564D;padding:2px 8px;margin-bottom:6px;}',
    '.agsp-prev-use{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-family:"Inter Tight",sans-serif;font-size:12.5px;line-height:1.45;color:#6B6B6B;margin-bottom:7px;}',
    '.agsp-prev-spec{margin-bottom:5px;}',
    '.agsp-prev-speclabel{display:block;font-family:Alata,sans-serif;font-size:11px;letter-spacing:0.1em;text-transform:uppercase;color:#6B6B6B;margin-bottom:0;}',
    '.agsp-prev-specval{display:block;font-family:"Inter Tight",sans-serif;font-size:12.5px;line-height:1.4;color:#242B26;}',
    '.agsp-prev-state{font-family:Alata,sans-serif;font-size:10.5px;letter-spacing:0.8px;text-transform:uppercase;margin-top:6px;}',
    '.agsp-prev-state.is-out{color:#7a3a3a;}',
    '.agsp-prev-state.is-na{color:#6B6B6B;}',
    '}',
    '@media (max-width:560px){',
    '.agsp-backdrop{align-items:flex-end;justify-content:stretch;}',
    '.agsp-panel{width:100%;max-height:100dvh;opacity:1;transform:translateY(100%);transition:transform 0.28s cubic-bezier(0.4,0,0.2,1);}',
    // r4 (A): dynamic scroll — the list caps at 3 full cells + a half-cell
    // peek (3×72 + 2×12 gaps + 12 + 42 = 294px) and scrolls inside; short
    // lists still hug (max-height never stretches). The peek is the scroll
    // affordance; the sticky foot keeps the CTA on screen.
    '.agsp-grid{grid-template-columns:1fr;max-height:294px;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;}',
    '.agsp-foot{position:sticky;bottom:0;background:#fff;border-top:1px solid rgba(0,0,0,0.1);}',
    '}',
    '@media (prefers-reduced-motion:reduce){.agsp-backdrop,.agsp-panel{transition:none;}}'
  ].join('\n');

  function ensureCss() {
    if (document.getElementById('agsp-css')) return;
    var s = document.createElement('style');
    s.id = 'agsp-css';
    s.textContent = AGSP_CSS;
    document.head.appendChild(s);
  }

  function money(cents) {
    var d = cents / 100;
    return '$' + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2)); // brand: no decimals at .00
  }

  function el(html) {
    var t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstChild;
  }

  var current = null; // { backdrop, cfg, selected:Set, inCart:Set }

  function close() {
    if (!current) return;
    document.removeEventListener('keydown', onKey, true);
    var b = current.backdrop;
    current = null;
    // animate out, then remove — interaction is dead immediately.
    b.classList.remove('is-open');
    b.style.pointerEvents = 'none';
    setTimeout(function () { b.remove(); }, 320);
  }

  function onKey(e) {
    if (e.key === 'Escape') { e.stopPropagation(); close(); }
  }

  // R4.0 (A's picker ruling, 2026-08-15) — the line under every finish name.
  //
  // ONE register inside the panel: everything "arrives" ("ships in" belongs to
  // the link, outside; A: "'ships in' and 'arrives in' are two different things,
  // so when they're in the same one it should say arrives"). Fulfilment mode is
  // read from a DATA marker the sync stamps on the sample product
  // (custom.sample_mode -> blob `mode`) — never inferred from copy, price or
  // counts (memory feedback_render_claim_needs_data_guarantee). An entry with no
  // mode promises nothing rather than guessing.
  function leadLine(s) {
    if (s.state === 'soldout') return { text: 'Out of stock', cls: ' is-out' };
    if (s.state === 'none') return { text: 'Unavailable', cls: ' is-na' };
    if (s.mode === 'domestic') return { text: 'Arrives within a week', cls: '' };
    if (s.mode === 'factory') return { text: 'Arrives in 2–3 weeks', cls: '' };
    return null;
  }

  function leadHtml(s) {
    var l = leadLine(s);
    return l ? '<span class="agsp-cell-lead' + l.cls + '">' + l.text + '</span>' : '';
  }

  // Footer line, all-domestic panel. A's R2.5 string was "Free shipping. Ships
  // out in 2–3 days." — SUPERSEDED 2026-08-15 (WG round) by "Free shipping.
  // Arrives within a week.": "ships out in" is a DISPATCH promise sitting three
  // rows under cells that make an ARRIVAL promise ("Arrives within a week"), and
  // two registers in one panel is how a truthful surface reads as a contradiction.
  // One register, one window, door-to-door.
  // The line still only survives an ALL-DOMESTIC panel. It cannot survive a
  // factory finish: a cell reading "Arrives in 2–3 weeks" three rows above a
  // footer promising a week is the same falsehood A's link ruling removed, in the
  // same panel. So the lead-time half drops exactly where the link's tail drops,
  // and the free-shipping half (true for every sample) always stays.
  // REVERSAL, if A wants the line shown everywhere: return CREDIT_DOMESTIC
  // unconditionally from this function — nothing else depends on it.
  var CREDIT_DOMESTIC = 'Free shipping. Arrives within a week.';

  function creditLine(samples) {
    var n = samples.length;
    var d = 0;
    for (var i = 0; i < n; i++) if (samples[i].mode === 'domestic') d++;
    return (n && d === n) ? CREDIT_DOMESTIC : 'Free shipping.';
  }

  function cellHtml(s, inCart) {
    var name = '<span class="agsp-cell-name">' + s.name +
      ' <span class="agsp-cell-code">(' + s.code + ')</span></span>';
    if (s.state === 'soldout' || s.state === 'none') {
      // R2.1 gray cell: disabled at render, no data-variant (invisible to
      // markSoldOut + the glue's 422 title map), no price. R4.0: its state text
      // moved into the shared lead line — same disabled/price-less/50%-swatch
      // treatment, one status position for every cell in the grid.
      return '<button type="button" class="agsp-cell" disabled>' +
        '<img class="agsp-cell-img" src="' + (s.image || '') + '" alt="">' +
        '<span class="agsp-cell-text"><span class="agsp-cell-main">' + name +
        leadHtml(s) + '</span></span></button>';
    }
    var flag = inCart ? '<div class="agsp-cell-flag">In cart</div>' : '';
    return '<button type="button" class="agsp-cell" data-variant="' + s.variantId + '"' +
      (inCart ? ' disabled' : '') + '>' +
      '<img class="agsp-cell-img" src="' + (s.image || '') + '" alt="">' +
      '<span class="agsp-cell-text"><span class="agsp-cell-main">' + name +
      leadHtml(s) + '</span>' +
      '<div class="agsp-cell-price">' + money(s.price) + '</div>' + flag + '</span></button>';
  }

  function updateCta() {
    var n = current.selected.size;
    var cta = current.backdrop.querySelector('.agsp-cta');
    cta.disabled = n === 0;
    // R25: the CTA total sums the SELECTED entries' real blob prices — the
    // cells already render money(s.price), so a hardcoded per-sample 500
    // lies the moment any sample product is not exactly $5.00.
    var total = 0;
    current.selected.forEach(function (vid) {
      var all = (current.cfg && current.cfg.samples) || [];
      for (var i = 0; i < all.length; i++) {
        if (all[i].variantId === vid) { total += all[i].price || 0; break; }
      }
    });
    cta.textContent = n === 0 ? 'SELECT SAMPLES'
      : 'ADD TO CART — ' + n + (n === 1 ? ' SAMPLE' : ' SAMPLES') + ' · ' + money(total);
  }

  function markSoldOut(variantId, description) {
    var cell = current.backdrop.querySelector('.agsp-cell[data-variant="' + variantId + '"]');
    if (cell) {
      cell.disabled = true;
      cell.classList.remove('is-selected');
      // R4.0: the live 422 race lands in the SAME status position as a
      // render-time out-of-stock cell, in A's wording ("we just say out of
      // stock") — not a second label in a second place.
      var main = cell.querySelector('.agsp-cell-main');
      var lead = cell.querySelector('.agsp-cell-lead');
      if (!lead && main) { lead = el('<span class="agsp-cell-lead"></span>'); main.appendChild(lead); }
      if (lead) { lead.className = 'agsp-cell-lead is-out'; lead.textContent = 'Out of stock'; }
    }
    current.selected.delete(variantId);
    var name = (current.cfg.samples.filter(function (x) { return x.variantId === variantId; })[0] || {}).name || 'That sample';
    current.backdrop.querySelector('.agsp-msg').textContent =
      name + ' is temporarily out of stock and was removed from your selection.';
    updateCta();
  }

  // R3-06: paint the preview card from a blob entry. Blank v3 fields hide
  // their row (mirrors the finish panel's caller-suppresses contract), so a
  // v2 blob degrades to photo + name + code without layout holes.
  function setPreview(s) {
    if (!current || !s) return;
    var prev = current.backdrop.querySelector('.agsp-prev');
    if (!prev) return;
    prev.querySelector('.agsp-prev-photo img').src = s.imageLarge || s.image || '';
    prev.querySelector('.agsp-prev-nametext').textContent = s.name;
    prev.querySelector('.agsp-prev-code').textContent = '(' + s.code + ')';
    var pill = prev.querySelector('.agsp-prev-pill');
    pill.textContent = s.toneLabel || '';
    pill.style.display = s.toneLabel ? '' : 'none';
    prev.querySelector('.agsp-prev-use').textContent = s.usecase || '';
    var tex = prev.querySelector('.agsp-prev-spec--texture');
    tex.style.display = s.texture ? '' : 'none';
    tex.querySelector('.agsp-prev-specval').textContent = s.texture || '';
    var mat = prev.querySelector('.agsp-prev-spec--material');
    mat.style.display = s.material ? '' : 'none';
    mat.querySelector('.agsp-prev-specval').textContent = s.material || '';
    var st = prev.querySelector('.agsp-prev-state');
    if (s.state === 'soldout') { st.hidden = false; st.className = 'agsp-prev-state is-out'; st.textContent = 'Sold out — temporarily unavailable'; }
    else if (s.state === 'none') { st.hidden = false; st.className = 'agsp-prev-state is-na'; st.textContent = 'No sample for this finish'; }
    else { st.hidden = true; }
  }

  function open(cfg) {
    ensureCss();
    close();
    // a previous instance may still be fading out — clear it so selectors
    // (and screen readers) only ever see one dialog.
    document.querySelectorAll('.agsp-backdrop').forEach(function (n) { n.remove(); });
    var backdrop = el('<div class="agsp-backdrop" role="dialog" aria-modal="true" aria-label="Order Finishing Samples">' +
      '<div class="agsp-panel">' +
      '<div class="agsp-head"><h2 class="agsp-title">Order Finishing Samples</h2>' +
      '<button type="button" class="agsp-close" aria-label="Close">×</button></div>' +
      '<p class="agsp-intro">Any sample costs are credited toward your furniture purchase.</p>' +
      '<div class="agsp-msg"></div>' +
      // R3-06: live panel card (desktop only — display:none <=560px). Fields
      // populate from setPreview; blank fields hide their row (panel behavior).
      '<div class="agsp-prev">' +
      '<div class="agsp-prev-photo"><img src="" alt=""></div>' +
      '<div class="agsp-prev-body">' +
      '<div class="agsp-prev-eyebrow">Finish</div>' +
      '<div class="agsp-prev-name"><span class="agsp-prev-nametext"></span><span class="agsp-prev-code"></span></div>' +
      '<div class="agsp-prev-divider"></div>' +
      '<div class="agsp-prev-tonerow"><span class="agsp-prev-pill"></span><span class="agsp-prev-use"></span></div>' +
      '<div class="agsp-prev-spec agsp-prev-spec--texture"><span class="agsp-prev-speclabel">Texture</span><span class="agsp-prev-specval"></span></div>' +
      '<div class="agsp-prev-spec agsp-prev-spec--material"><span class="agsp-prev-speclabel">Material</span><span class="agsp-prev-specval"></span></div>' +
      '<div class="agsp-prev-state" hidden></div>' +
      '</div></div>' +
      '<div class="agsp-grid"></div>' +
      // R2.2 (A): shipping line sits ABOVE the CTA for visibility. Copy: A verbatim 2026-07-18.
      '<div class="agsp-foot"><p class="agsp-credit">' + creditLine(cfg.samples) + '</p>' +
      '<button type="button" class="agsp-cta" disabled>SELECT SAMPLES</button></div>' +
      '</div></div>');
    document.body.appendChild(backdrop);
    current = { backdrop: backdrop, cfg: cfg, selected: new Set(), inCart: new Set() };

    // R2.2: cells render SYNCHRONOUSLY from the blob — the panel opens at its
    // final size (no post-open grid jump; swatch imgs sit in fixed 52px boxes).
    // R3-06: each cell carries its render-order index so the preview card can
    // resolve hover/click back to the blob entry (additive attribute —
    // markSoldOut / the in-cart pass / the glue's 422 map are untouched).
    var grid = backdrop.querySelector('.agsp-grid');
    var agAll = cfg.samples.concat(cfg.unavailable || []);
    cfg.samples.forEach(function (s, i) {
      var c = el(cellHtml(s, false));
      c.setAttribute('data-agsp-idx', i);
      grid.appendChild(c);
    });
    // R2.1: grays render AFTER orderables (orderable-first order).
    (cfg.unavailable || []).forEach(function (s, i) {
      var c = el(cellHtml(s));
      c.setAttribute('data-agsp-idx', cfg.samples.length + i);
      grid.appendChild(c);
    });

    // R3-06: the preview follows hover AND click (click = tap/keyboard parity);
    // initial paint = the first orderable, synchronously (no post-open pop-in).
    function previewFromEvent(e) {
      var cell = e.target.closest && e.target.closest('.agsp-cell');
      if (!cell || !cell.hasAttribute('data-agsp-idx')) return;
      setPreview(agAll[parseInt(cell.getAttribute('data-agsp-idx'), 10)]);
    }
    grid.addEventListener('mouseover', previewFromEvent);
    grid.addEventListener('click', previewFromEvent);
    if (cfg.samples.length) setPreview(cfg.samples[0]);

    // entrance: class one frame after mount so the transition runs.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { backdrop.classList.add('is-open'); });
    });

    // in-cart state is a DECORATION pass — it never gates the render. If the
    // read resolves after a fast first click, the affected cell is deselected.
    cfg.cart.read().then(function (c) {
      if (!current || current.backdrop !== backdrop) return; // closed/reopened meanwhile
      (c.items || []).forEach(function (it) { current.inCart.add(it.id); });
      var changed = false;
      cfg.samples.forEach(function (s) {
        if (!current.inCart.has(s.variantId)) return;
        var cell = grid.querySelector('.agsp-cell[data-variant="' + s.variantId + '"]');
        if (!cell || cell.disabled) return;
        cell.disabled = true;
        cell.classList.remove('is-selected');
        if (current.selected.delete(s.variantId)) changed = true;
        if (!cell.querySelector('.agsp-cell-flag')) {
          cell.querySelector('span').appendChild(el('<div class="agsp-cell-flag">In cart</div>'));
        }
      });
      if (changed) updateCta();
    }).catch(function () {}); // cart read failure must never blank the picker

    grid.addEventListener('click', function (e) {
      var cell = e.target.closest('.agsp-cell');
      if (!cell || cell.disabled || !current) return;
      var id = parseInt(cell.dataset.variant, 10);
      if (current.selected.has(id)) { current.selected.delete(id); cell.classList.remove('is-selected'); }
      else { current.selected.add(id); cell.classList.add('is-selected'); } // qty-1-each: a toggle, no qty UI
      updateCta();
    });

    backdrop.querySelector('.agsp-cta').addEventListener('click', function () {
      if (!current || current.selected.size === 0) return;
      var cta = backdrop.querySelector('.agsp-cta');
      cta.disabled = true;
      // pre-add diff against a FRESH cart read (another tab may have added one)
      cfg.cart.read().then(function (c) {
        var have = {};
        (c.items || []).forEach(function (it) { have[it.id] = true; });
        var items = Array.from(current.selected)
          .filter(function (id) { return !have[id]; })
          .map(function (id) { return { id: id, quantity: 1 }; });
        if (!items.length) { close(); if (cfg.onAdded) cfg.onAdded([]); return; }
        cfg.cart.add(items).then(function () {
          close();
          if (cfg.onAdded) cfg.onAdded(items);
        }).catch(function (err) {
          cta.disabled = false;
          updateCta();
          if (err && err.status === 422 && err.variantId) markSoldOut(err.variantId, err.description);
          else backdrop.querySelector('.agsp-msg').textContent =
            (err && err.description) || 'Something went wrong — please try again.';
        });
      });
    });

    backdrop.querySelector('.agsp-close').addEventListener('click', close);
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });
    document.addEventListener('keydown', onKey, true);
  }

  function directAdd(cfg) {
    cfg.cart.read().then(function (c) {
      var have = (c.items || []).some(function (it) { return it.id === cfg.sample.variantId; });
      if (have) { if (cfg.onAdded) cfg.onAdded([]); return; }
      cfg.cart.add([{ id: cfg.sample.variantId, quantity: 1 }]).then(function () {
        if (cfg.onAdded) cfg.onAdded([{ id: cfg.sample.variantId, quantity: 1 }]);
      }).catch(function (err) {
        if (cfg.onSoldOut) cfg.onSoldOut(err);
      });
    });
  }

  window.AG_SAMPLE_PICKER = { open: open, directAdd: directAdd, close: close };
})();
// ===========================================================================
// THEME GLUE (sample-request R2 Gate 4) — everything below this line replaces
// the prototype's mock layer (sample-data.js + sc2.js glue). The component
// above is byte-identical to prototypes/shared/sample-picker.js.
// ===========================================================================
(function () {
  'use strict';

  function realCart(samples) {
    return {
      add: function (items) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          if (r.ok) return r.json();
          return r.json().catch(function () { return {}; }).then(function (b) {
            var err = { status: r.status, message: b.message || 'Cart Error',
                        description: b.description || '' };
            // Shopify's 422 description names the product title — map it back to a
            // variantId so the picker can flip the exact cell to SOLD OUT.
            for (var i = 0; i < samples.length; i++) {
              var title = 'Finish: ' + samples[i].name + ' ' + samples[i].code;
              if (err.description.indexOf(title) !== -1) { err.variantId = samples[i].variantId; break; }
            }
            throw err;
          });
        });
      },
      read: function () {
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
          return { items: (c.items || []).map(function (i) { return { id: i.variant_id || i.id }; }) };
        });
      }
    };
  }

  function openDrawer() {
    var d = document.querySelector('cart-drawer');
    if (d && typeof d.open === 'function') { d.open(); return true; }
    return false;
  }

  function init() {
    var wrap = document.querySelector('.ag-sample-link-wrap');
    if (!wrap) return;
    // --plain rail placement (Datch 2026-09-09): on the Options card, the
    // non-customizable link moves INTO the finishing grid so it can sit in the
    // label rail directly under the FINISHING heading (placement rules in
    // ag-options-card.css key on this parent). Customizable products keep the
    // footer band; non-card layouts (Lounge/Workstations) are untouched.
    var finOpt = document.querySelector('.product-header_option-list.ag-optcard .ag-finish-option');
    if (finOpt && wrap.classList.contains('ag-sample-link-wrap--plain')) {
      finOpt.appendChild(wrap);
      // Bottom-anchor: the rail cell's height runs from the row top to the visible
      // tile's bottom edge, which varies per product - measure it and hand it to the
      // CSS (height: var(--ag-rail-h)). The card button is CSS-sized, so this is
      // stable before images finish loading. Mobile CSS sets height:auto and ignores it.
      var setRailHeight = function () {
        // anchor to the IMAGE crop window (.ag-finish-card-img-wrap), not the card
        // button - the button's box runs on past the image through the name caption
        // (Datch 2026-09-09: bottom aligns with the finishing selector image)
        var card = finOpt.querySelector('.ag-finish-card-img-wrap') || finOpt.querySelector('.ag-finish-card');
        if (!card) return;
        // measure from the wrap's OWN top (its grid cell starts below the row's
        // padding; align-self:start keeps that top stable whatever height we set)
        var px = Math.round(card.getBoundingClientRect().bottom - wrap.getBoundingClientRect().top);
        if (px > 0) wrap.style.setProperty('--ag-rail-h', px + 'px');
      };
      setRailHeight();
      window.addEventListener('resize', setRailHeight);
    }
    var blob = document.getElementById('sample-request-data-' + wrap.getAttribute('data-product-id'));
    if (!blob) return;
    var data, samples, grays;
    try { data = JSON.parse(blob.textContent) || {}; } catch (e) { return; }
    samples = data.samples || [];
    grays = data.unavailable || []; // R2.1 blob v2 — grayed cells (soldout/none)
    if (!samples.length) return;    // link never renders at 0 orderable (D2) — belt-and-braces

    var link = wrap.querySelector('.ag-sample-link');
    var msg = wrap.querySelector('.ag-sample-link-msg');
    var cart = realCart(samples);

    // R2.2: warm the swatch images at idle so the picker opens with finishes
    // already decoded (locked preload discipline: idle/hover, never eager).
    // R3-06: also warm imageLarge (the 800w preview-card tier) so hovering
    // cells swaps the panel photo without a fetch.
    var warm = window.requestIdleCallback || function (fn) { setTimeout(fn, 1500); };
    warm(function () {
      samples.concat(grays).forEach(function (s) {
        if (s.image) { var i = new Image(); i.src = s.image; }
        if (s.imageLarge && s.imageLarge !== s.image) { var j = new Image(); j.src = s.imageLarge; }
      });
    });

    function setInert(name) {
      // name kept in the signature for callers; not shown (Datch 2026-09-09: no
      // finish names in card sample copy - the cart line already names it)
      link.classList.add('is-inert');
      link.textContent = 'SAMPLE IN CART';
    }

    function confirmAdd(items) {
      if (openDrawer()) return;
      msg.hidden = false;
      msg.classList.remove('is-err');
      msg.textContent = items.length
        ? 'Added to cart: ' + items.length + (items.length === 1 ? ' sample.' : ' samples.')
        : 'Already in your cart.';
    }

    // Pre-existing in-cart state for the exactly-1 DIRECT-ADD link — deferred to
    // idle (never block render; locked preload discipline). R2.1: with grays the
    // link opens the picker instead, so it must never go inert — IN CART renders
    // as a disabled cell inside the picker.
    if (samples.length === 1 && !grays.length) {
      var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 1500); };
      idle(function () {
        cart.read().then(function (c) {
          if (c.items.some(function (it) { return it.id === samples[0].variantId; })) {
            setInert(samples[0].name);
          }
        }).catch(function () {});
      });
    }

    link.addEventListener('click', function (e) {
      e.preventDefault();
      if (link.classList.contains('is-inert')) return;
      // R2.1 (D3): the picker opens whenever there are grays to communicate;
      // direct-add survives only at exactly-1 orderable AND zero grays.
      if (samples.length >= 2 || grays.length) {
        window.AG_SAMPLE_PICKER.open({ samples: samples, unavailable: grays, cart: cart, onAdded: confirmAdd });
      } else {
        window.AG_SAMPLE_PICKER.directAdd({
          sample: samples[0], cart: cart,
          onAdded: function (items) { setInert(samples[0].name); if (items.length) openDrawer(); },
          onSoldOut: function () {
            msg.hidden = false;
            msg.classList.add('is-err');
            msg.textContent = 'This sample is temporarily out of stock.';
          }
        });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
